import React, { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import {
  MessageSquare, Send, CheckCircle, Clock, Users,
  RefreshCw, ChevronDown, ChevronUp, AlertCircle
} from 'lucide-react';
import { differenceInDays, format } from 'date-fns';
import useClients from '../hooks/useClients';
import useBookings from '../hooks/useBookings';
import { supabase } from '../../supabase';

const VCARD = 'https://monarchdetailing.art/monarch.vcf';

const TEMPLATES = [
  {
    id: 'checkin',
    label: 'Casual Check-In',
    emoji: '👋',
    text: (name) =>
      `Hey ${name}! It's Akeem from Monarch Detailing. Haven't seen you in a minute — how's the car holding up? I've got some openings this week if you're ready to get it fresh again. 🦋 Save my contact 👉 ${VCARD}`,
  },
  {
    id: 'value',
    label: 'Value Reminder',
    emoji: '✨',
    text: (name) =>
      `Hey ${name}, Akeem here. It's been a while since your last detail and I just wanted to check in. Ready to get that clean feeling back? I can fit you in this week — just reply and I'll get you set up. Save my contact 👉 ${VCARD}`,
  },
  {
    id: 'exclusive',
    label: 'Exclusive Offer',
    emoji: '🎁',
    text: (name) =>
      `Hey ${name}! Akeem from Monarch. Since it's been a while, I want to take care of you — reply back and I'll hook you up with a little something extra on your next detail. Only for returning clients. 🦋 Save my contact 👉 ${VCARD}`,
  },
  {
    id: 'season',
    label: 'Seasonal Push',
    emoji: '🍂',
    text: (name) =>
      `Hey ${name}! Akeem here. With the season changing, now's the perfect time to deep clean your interior before [things get messy / the cold sets in]. Got spots this week — want me to swing by? Save my contact 👉 ${VCARD}`,
  },
];

const THRESHOLD_OPTIONS = [
  { label: '45+ days', days: 45 },
  { label: '60+ days', days: 60 },
  { label: '90+ days', days: 90 },
];

function daysSince(dateStr) {
  if (!dateStr) return null;
  return differenceInDays(new Date(), new Date(dateStr));
}

function urgencyColor(days) {
  if (days === null) return 'bg-gray-100 text-gray-500';
  if (days >= 90) return 'bg-red-100 text-red-700';
  if (days >= 60) return 'bg-orange-100 text-orange-700';
  return 'bg-yellow-100 text-yellow-800';
}

export default function WinBack() {
  const { clients, loading: clientsLoading } = useClients();
  const { bookings, loading: bookingsLoading } = useBookings();
  const loading = clientsLoading || bookingsLoading;

  const lastBookingByClient = useMemo(() => {
    const map = {};
    bookings.forEach(b => {
      if (!b.client_id) return;
      const date = b.scheduled_at || b.service_date || b.created_at;
      if (!date) return;
      if (!map[b.client_id] || new Date(date) > new Date(map[b.client_id])) {
        map[b.client_id] = date;
      }
    });
    return map;
  }, [bookings]);

  const [threshold, setThreshold] = useState(45);
  const [selectedTemplate, setSelectedTemplate] = useState('checkin');
  const [messages, setMessages] = useState({});
  const [sending, setSending] = useState({});
  const [sent, setSent] = useState({});
  const [errors, setErrors] = useState({});
  const [expandedId, setExpandedId] = useState(null);

  const template = TEMPLATES.find(t => t.id === selectedTemplate) || TEMPLATES[0];

  const inactive = useMemo(() => {
    return clients
      .filter(c => {
        const lastDate = lastBookingByClient[c.id] || c.last_service_date;
        const days = daysSince(lastDate);
        if (days === null) return true;
        return days >= threshold;
      })
      .filter(c => c.phone)
      .map(c => {
        const lastDate = lastBookingByClient[c.id] || c.last_service_date;
        return {
          ...c,
          resolvedLastService: lastDate || null,
          daysSince: daysSince(lastDate),
        };
      })
      .sort((a, b) => {
        const da = a.daysSince ?? 9999;
        const db = b.daysSince ?? 9999;
        return db - da;
      });
  }, [clients, lastBookingByClient, threshold]);

  function getMessageForClient(client) {
    return messages[client.id] !== undefined
      ? messages[client.id]
      : template.text(client.name?.split(' ')[0] || 'there');
  }

  function applyTemplateToAll() {
    const updated = {};
    inactive.forEach(c => {
      updated[c.id] = template.text(c.name?.split(' ')[0] || 'there');
    });
    setMessages(updated);
  }

  async function sendToClient(client) {
    const message = getMessageForClient(client);
    if (!message.trim()) return;

    setSending(s => ({ ...s, [client.id]: true }));
    setErrors(e => ({ ...e, [client.id]: null }));

    try {
      const { data: { session } } = await supabase.auth.getSession();
      const res = await fetch('/api/send-admin-sms', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session?.access_token}`,
        },
        body: JSON.stringify({
          type: 'winback',
          clientPhone: client.phone,
          message,
          clientId: client.id,
        }),
      });

      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.error || 'Failed to send');
      }

      setSent(s => ({ ...s, [client.id]: true }));
    } catch (err) {
      setErrors(e => ({ ...e, [client.id]: err.message }));
    } finally {
      setSending(s => ({ ...s, [client.id]: false }));
    }
  }

  async function sendToAll() {
    const unsentClients = inactive.filter(c => !sent[c.id] && !sending[c.id]);
    for (const client of unsentClients) {
      await sendToClient(client);
      await new Promise(r => setTimeout(r, 400));
    }
  }

  const sentCount = Object.values(sent).filter(Boolean).length;

  return (
    <div className="p-6 pb-24 md:pb-6 space-y-6 max-w-4xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h2 className="text-xl font-bold text-gray-900">Win-Back Campaigns</h2>
          <p className="text-sm text-gray-500 mt-0.5">Re-engage clients who haven't booked recently</p>
        </div>
        <div className="flex items-center gap-2 text-sm text-gray-500 bg-white border border-gray-200 rounded-lg px-3 py-2">
          <Users size={15} />
          <span>{inactive.length} clients need attention</span>
        </div>
      </div>

      {/* Stats strip */}
      <div className="grid grid-cols-3 gap-3">
        {THRESHOLD_OPTIONS.map(opt => {
          const count = clients.filter(c => {
            const lastDate = lastBookingByClient[c.id] || c.last_service_date;
            const d = daysSince(lastDate);
            return (d === null || d >= opt.days) && c.phone;
          }).length;
          return (
            <button
              key={opt.days}
              onClick={() => setThreshold(opt.days)}
              className={`rounded-xl p-4 text-left border transition-all ${
                threshold === opt.days
                  ? 'bg-[#C9A84C] border-[#C9A84C] text-black'
                  : 'bg-white border-gray-200 text-gray-700 hover:border-[#C9A84C]'
              }`}
            >
              <p className="text-2xl font-bold">{count}</p>
              <p className="text-xs font-medium mt-0.5 opacity-75">{opt.label}</p>
            </button>
          );
        })}
      </div>

      {/* Template picker */}
      <div className="bg-white rounded-2xl border border-gray-200 p-5 space-y-3">
        <div className="flex items-center justify-between">
          <h3 className="font-semibold text-gray-900 text-sm">Message Template</h3>
          <button
            onClick={applyTemplateToAll}
            className="text-xs text-[#C9A84C] font-medium hover:underline flex items-center gap-1"
          >
            <RefreshCw size={12} />
            Apply to all
          </button>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
          {TEMPLATES.map(t => (
            <button
              key={t.id}
              onClick={() => setSelectedTemplate(t.id)}
              className={`p-3 rounded-xl text-left border text-xs font-medium transition-all ${
                selectedTemplate === t.id
                  ? 'bg-[#C9A84C] border-[#C9A84C] text-black'
                  : 'bg-gray-50 border-gray-200 text-gray-700 hover:border-[#C9A84C]'
              }`}
            >
              <div className="text-base mb-1">{t.emoji}</div>
              {t.label}
            </button>
          ))}
        </div>
        {/* Preview */}
        <div className="bg-gray-50 rounded-xl p-3 text-sm text-gray-600 italic">
          "{template.text('[Name]')}"
        </div>
      </div>

      {/* Send all button */}
      {inactive.length > 0 && (
        <div className="flex items-center justify-between bg-amber-50 border border-amber-200 rounded-xl px-4 py-3">
          <div>
            <p className="text-sm font-semibold text-amber-800">
              {sentCount > 0 ? `${sentCount}/${inactive.length} sent this session` : `${inactive.length} clients ready to message`}
            </p>
            <p className="text-xs text-amber-600 mt-0.5">Each message is personalized with their first name</p>
          </div>
          <button
            onClick={sendToAll}
            disabled={sentCount === inactive.length}
            className="bg-amber-500 text-white px-4 py-2 rounded-lg text-sm font-semibold hover:bg-amber-600 disabled:opacity-40 disabled:cursor-not-allowed flex items-center gap-2"
          >
            <Send size={14} />
            {sentCount === inactive.length ? 'All Sent' : 'Send All'}
          </button>
        </div>
      )}

      {/* Client list */}
      {loading ? (
        <div className="flex justify-center py-16">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#C9A84C]" />
        </div>
      ) : inactive.length === 0 ? (
        <div className="text-center py-16 text-gray-400">
          <CheckCircle size={40} className="mx-auto mb-3 text-emerald-400" />
          <p className="font-semibold text-gray-600">All caught up!</p>
          <p className="text-sm mt-1">No clients have been inactive for {threshold}+ days.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {inactive.map(client => {
            const isExpanded = expandedId === client.id;
            const isSent = sent[client.id];
            const isSending = sending[client.id];
            const error = errors[client.id];
            const msg = getMessageForClient(client);

            return (
              <div
                key={client.id}
                className={`bg-white rounded-2xl border transition-all ${
                  isSent ? 'border-emerald-300' : 'border-gray-200'
                }`}
              >
                <div className="flex items-center gap-3 px-4 py-3">
                  {/* Avatar */}
                  <div className="w-9 h-9 rounded-full bg-[#C9A84C] flex items-center justify-center text-black text-xs font-bold shrink-0">
                    {(client.name || '?').split(' ').map(w => w[0]).join('').toUpperCase().slice(0, 2)}
                  </div>

                  {/* Info */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <Link to={`/admin/clients/${client.id}`} className="font-semibold text-gray-900 text-sm hover:text-[#C9A84C]">
                        {client.name}
                      </Link>
                      <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${urgencyColor(client.daysSince)}`}>
                        {client.daysSince === null
                          ? 'Never booked'
                          : `${client.daysSince}d ago · ${format(new Date(client.resolvedLastService), 'MMM d, yyyy')}`}
                      </span>
                      {client.last_winback_sms_at && !isSent && (
                        <span className="text-[10px] text-gray-400 flex items-center gap-1">
                          <Clock size={10} />
                          Last messaged {format(new Date(client.last_winback_sms_at), 'MMM d')}
                        </span>
                      )}
                    </div>
                    <p className="text-xs text-gray-400 mt-0.5 truncate">{client.phone}</p>
                  </div>

                  {/* Actions */}
                  <div className="flex items-center gap-2 shrink-0">
                    {isSent ? (
                      <span className="flex items-center gap-1.5 text-emerald-600 text-xs font-semibold">
                        <CheckCircle size={16} /> Sent
                      </span>
                    ) : (
                      <>
                        <button
                          onClick={() => setExpandedId(isExpanded ? null : client.id)}
                          className="p-1.5 text-gray-400 hover:text-gray-700 rounded-lg hover:bg-gray-100"
                          title="Edit message"
                        >
                          {isExpanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                        </button>
                        <button
                          onClick={() => sendToClient(client)}
                          disabled={isSending}
                          className="flex items-center gap-1.5 bg-[#C9A84C] text-black px-3 py-1.5 rounded-lg text-xs font-semibold hover:bg-[#b8943d] disabled:opacity-50"
                        >
                          {isSending ? (
                            <div className="w-3 h-3 border-2 border-black/30 border-t-black rounded-full animate-spin" />
                          ) : (
                            <MessageSquare size={13} />
                          )}
                          {isSending ? 'Sending…' : 'Send'}
                        </button>
                      </>
                    )}
                  </div>
                </div>

                {/* Expanded message editor */}
                {isExpanded && !isSent && (
                  <div className="px-4 pb-4 pt-0 border-t border-gray-100">
                    <label className="block text-xs font-medium text-gray-500 mb-1.5 mt-3">
                      Customize message for {client.name?.split(' ')[0]}
                    </label>
                    <textarea
                      rows={4}
                      value={msg}
                      onChange={e => setMessages(m => ({ ...m, [client.id]: e.target.value }))}
                      className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm text-gray-800 focus:outline-none focus:ring-2 focus:ring-[#C9A84C] resize-none"
                    />
                    <div className="flex justify-between items-center mt-2">
                      <button
                        onClick={() => setMessages(m => ({ ...m, [client.id]: template.text(client.name?.split(' ')[0] || 'there') }))}
                        className="text-xs text-gray-400 hover:text-gray-600"
                      >
                        Reset to template
                      </button>
                      <span className="text-xs text-gray-400">{msg.length} chars</span>
                    </div>
                  </div>
                )}

                {/* Error */}
                {error && (
                  <div className="px-4 pb-3 flex items-center gap-2 text-xs text-red-600">
                    <AlertCircle size={13} />
                    {error}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
